import { RATING_MAX, RATING_MIN, RATING_NEUTRAL } from './constants.js';

/**
 * A player's rating for one game, out of ten.
 *
 * It used to be derived: the group tapped one of five faces, and this function
 * stretched that onto a ten-point scale and added a bonus for goals and assists
 * to recover some of the resolution the five steps had thrown away.
 *
 * Now the group rates out of ten directly, so there is nothing to derive. The
 * number here is the number they gave. The attacking bonus went with the
 * stretching: the people rating had already watched the goals go in, and adding
 * points for them on top counted the same event twice — a striker who scored
 * twice and did nothing else got credited for it by every rater and then again
 * by the app. Goals and assists still show on the token as badges, which is
 * where a fact belongs rather than folded into an opinion.
 *
 * Returns null where nobody has rated the player. That's not a zero and not a
 * middling score — it's the absence of one, and the screen prints a dash.
 */
export function matchRating({ performance = RATING_NEUTRAL, goals = 0, assists = 0, rated } = {}) {
  if (rated === false) return null;
  const score = Number(performance ?? RATING_NEUTRAL);
  if (!Number.isFinite(score)) return null;
  return Math.max(RATING_MIN, Math.min(RATING_MAX, Number(score.toFixed(1))));
}

/**
 * Colour band, so a team sheet reads at a glance instead of digit by digit.
 * Pitched against what a 1-10 rating actually means in a match report: 6 is a
 * fine game, 7 is a good one, 8 upwards is the reason you remember it.
 */
export function ratingTone(rating) {
  // No rating is not a bad rating. Without this, an unrated player's token comes
  // out the same colour as someone the group said had a shocker.
  if (rating == null) return null;
  if (rating >= 8) return 'great';
  if (rating >= 7) return 'good';
  if (rating >= RATING_NEUTRAL) return 'ok';
  return 'poor';
}
